/**
 * Landing v2 tokens · Blue-to-Orange accessible data scale.
 * Never use raw red/green for financial metrics or BeCo scores.
 */
export const MDX = {
    // Backgrounds
    bgDark:   "#090D16",
    bgLight:  "#FFFFFF",
    textDark: "#090D16",
    textLite: "#F9FAFB",
    borderDk: "#1F2937",
    borderLt: "#E5E7EB",

    // Accessible data scale (red-to-orange · Maasai red is canonical brand accent).
    // `blueLow` resolves through a CSS variable so it auto-lightens in dark mode
    // to preserve WCAG 4.5:1 contrast on near-black surfaces (#0A0A0A). Falls back
    // to the raw hex if the runtime doesn't support CSS custom properties (SSR / older
    // browsers).
    blueLow:  "var(--brand-red-fg, #922529)",
    blueLowSolid: "#922529",  // Use for surfaces (buttons, chips) — not for text.
    grayMid:  "#6B7280",
    orangeHi: "#EA580C",
    greenCta: "var(--brand-green-fg, #006600)",  // Adaptive — text usage.
    greenCtaSolid: "#006600",  // Use for button backgrounds.
    greenHov: "#004d00",

    // Sparing signal accents (10% / 15% red tints)
    tint10:   "rgba(146,37,41,0.10)",
    tint15:   "rgba(146,37,41,0.20)",
    warn10:   "rgba(234,88,12,0.10)",
    warn15:   "rgba(234,88,12,0.15)",
};

/** 6-language selector — replaces legacy (EN/SW/SH) trio site-wide. */
export const LANGS = [
    { code: "EN", label: "English"    },
    { code: "SW", label: "Kiswahili"  },
    { code: "SH", label: "Sheng"      },
    { code: "FR", label: "Français"   },
    { code: "AR", label: "العربية"    },
    { code: "PT", label: "Português"  },
];

/** 33-country · 21-exchange Pan-African footprint. */
export const EXCHANGES_21 = [
    { code: "EGX",   country: "Egypt",           tickers: ["COMI","HRHO"] },
    { code: "JSE",   country: "South Africa",    tickers: ["MTN","SBK","NPN"] },
    { code: "NGX",   country: "Nigeria",         tickers: ["DANGCEM","MTNN"] },
    { code: "NSE",   country: "Kenya",           tickers: ["SCOM","EQTY","KCB"] },
    { code: "BRVM",  country: "8-country union", tickers: ["SNTS","BOAB"] },
    { code: "BVMAC", country: "CEMAC region",    tickers: ["SEMC"] },
    { code: "GSE",   country: "Ghana",           tickers: ["MTNGH","GCB"] },
    { code: "MASI",  country: "Morocco",         tickers: ["ATW","IAM"] },
    { code: "DSE",   country: "Tanzania",        tickers: ["CRDB","NMB"] },
    { code: "USE",   country: "Uganda",          tickers: ["MTN","SBU"] },
    { code: "RSE",   country: "Rwanda",          tickers: ["BOK","BLR"] },
    { code: "ESE",   country: "Ethiopia",        tickers: []            },
    { code: "LSE",   country: "Lusaka · Zambia", tickers: ["ZANACO"]    },
    { code: "NSX",   country: "Namibia",         tickers: ["FNB","NBS"] },
    { code: "MERJ",  country: "Seychelles",      tickers: []            },
    { code: "MSM",   country: "Malawi",          tickers: ["NBM"]       },
    { code: "ZSE",   country: "Zimbabwe",        tickers: ["ECO","DZL"] },
    { code: "VFEX",  country: "Zim · Victoria",  tickers: ["PPCZW"]     },
    { code: "BVI",   country: "Cape Verde",      tickers: []            },
    { code: "BSE",   country: "Botswana",        tickers: ["FNBB"]      },
    { code: "SEM",   country: "Mauritius",       tickers: ["MCB","SBM"] },
];

/** Trading habits that help you (the "good habits" tab).
 *  Public copy: plain-English name for each habit + what it means for a
 *  beginner + how the app helps you build it. No formulas, no jargon. */
export const ALPHA_B_METRICS = [
    { id: "alpha1", key: "Habit 1", name: "Sticking To Your Plan",
      principle: "Changing your mind at the last second",
      explains:  "You plan to buy at one price, then talk yourself into a 'better' price and miss it. This checks how closely you actually follow the plan you started with.",
      counter:   "If the price moves a lot right before you trade, we default you to a fixed-price order — so you can't chase it on a feeling." },
    { id: "alpha2", key: "Habit 2", name: "Cutting Losses Early",
      principle: "Holding on and hoping",
      explains:  "Losing money hurts more than winning feels good, so people hold on to a losing trade hoping it bounces back. This checks how often you set — and keep — a safety stop (a price where you automatically sell to limit a loss).",
      counter:   "Every trade asks you to set a safety stop before you can buy, and once it's set, you can't move it further into a loss." },
    { id: "alpha3", key: "Habit 3", name: "Betting The Same Amount Each Time",
      principle: "Getting overconfident after a win",
      explains:  "After a couple of wins, people quietly double their next bet. This checks how steady your trade sizes are over your last twenty trades.",
      counter:   "We suggest a safe amount for each trade and stop you from betting more than a set share of your account, no matter how good the last few trades felt." },
    { id: "alpha4", key: "Habit 4", name: "Trading On A Plan, Not A Feeling",
      principle: "Fear of missing out",
      explains:  "Seeing a price jump on a chat group is not a plan. This checks how many of your trades came from a written reason versus a quick, excited click.",
      counter:   "If you try to buy right after a big, sudden price jump, we add a short cool-down before the order can go through." },
    { id: "alpha5", key: "Habit 5", name: "Writing Down Why, Before You Trade",
      principle: "Rewriting the story afterwards",
      explains:  "After a trade works out, it's easy to convince yourself you always knew it would. This locks in your real reason for the trade before you enter, so you can look back honestly later.",
      counter:   "You must write your reason before the trade unlocks, and once submitted, it can't be edited — no rewriting history." },
];

/** Trading risks to watch (the "watch out for" tab). */
export const SIGMA_B_METRICS = [
    { id: "sigma1", key: "Risk 1", name: "Losing Money Too Fast",
      principle: "Chasing your losses",
      explains:  "When you're losing money, it's tempting to double your bet and try to win it back fast. This watches how quickly your money is falling, not just how much.",
      counter:   "If your losses start speeding up, we shrink your order sizes and add a short pause between trades." },
    { id: "sigma2", key: "Risk 2", name: "Too Much Money In One Place",
      principle: "Sticking to what feels familiar",
      explains:  "People put too much money into the stocks they hear about most, because it feels 'safe'. This checks how much of your money is riding on one company.",
      counter:   "We warn you above one-third of your money in a single stock, and block new trades once one position passes half your account." },
    { id: "sigma3", key: "Risk 3", name: "Risk You Carry Overnight",
      principle: "Forgetting that news doesn't sleep",
      explains:  "Markets close, but news keeps happening. This checks how much risk you're holding during hours when you can't react to it.",
      counter:   "Fifteen minutes before the market closes, we ask you to decide: keep the position open overnight, or close it now." },
    { id: "sigma4", key: "Risk 4", name: "Hidden Sameness In Your Bets",
      principle: "Thinking you're spread out when you're not",
      explains:  "Ten different stocks can still be one big bet if they all move together. This checks how connected your trades really are.",
      counter:   "When your positions start moving in lockstep, we block new similar trades until you spread your money out again." },
    { id: "sigma5", key: "Risk 5", name: "Borrowing Too Much To Trade",
      principle: "Feeling unstoppable after a winning streak",
      explains:  "A run of wins can feel like skill — until it ends. This checks how much borrowed money you're trading with compared to what's really yours.",
      counter:   "After five wins in a row, we automatically cap how much you can borrow for the rest of the day." },
];
